// @ts-nocheck
import React from 'react'
import { Card, CardContent, CardMedia } from '@material-ui/core'
import styled from 'styled-components'
import IconComponent from '../../../themes/IconComponent'
import TypographyPro from '../../../themes/TypographyPro'
import ItemPrice from '../../../components/menu/ItemPrice'
import { mediaQueriesBreakpoints, respondAbove } from 'styles/mixins'
import { getLocaleStr } from 'utils/utils'
import { inject, observer } from 'mobx-react'
import { kebabCase } from 'lodash-es'

const imageAspectRatio = '16 / 10'

const StyledCard = styled(Card)`
	background-color: var(--clear);
	border-radius: 4px;
	box-shadow: var(--defaultShadow2);
	transition: box-shadow 0.5s ease-in-out;
	width: 260px;
	cursor: pointer;
	position: relative;
	overflow: hidden;
	flex: none;

	& .lto-media {
		aspect-ratio: ${imageAspectRatio};
		transition: transform 1s;
	}

	&::after {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		box-shadow: 0 0 0 0 rgba(0, 0, 0, 0) inset;
		transition: box-shadow 0.5s ease;
	}

	&:active {
		box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);

		& .lto-media {
			transform: scale(1.05);
		}

		&::after {
			box-shadow: var(--innerShadow);
		}
	}

	@media (hover: hover) {
		&:hover {
			box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);

			& .lto-media {
				transform: scale(1.05);
			}

			&::after {
				box-shadow: var(--innerShadow);
			}
		}
	}

	${respondAbove.md`
  	    width: 360px;
  	`}

	${respondAbove.lg`
  	     width: 385px;
  	`}
`

// used to avoid image overflowing borders when hovering it
const CardMediaContainer = styled.div`
	width: 100%;
	aspect-ratio: ${imageAspectRatio};
	overflow: hidden;
`

const StyledCardContent = styled(CardContent)`
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 20px;

	.text-two-lines {
		/* force text to take up two lines and instead of overflow have '...' */
		overflow: hidden;
		text-overflow: ellipsis;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		-webkit-box-orient: vertical;
	}

	& .lto-title {
		color: var(--strokeMenuTitle);
		margin-bottom: 5px;
		line-height: 18px;
		height: 36px;
	}

	& .lto-description {
		color: var(--inactive);
		margin-bottom: 20px;
		line-height: 16px;
		height: 32px;
	}

	@media ${mediaQueriesBreakpoints.md} {
		& .lto-title {
			line-height: 21px;
			height: 42px;
		}

		& .lto-description {
			line-height: 19px;
			height: 38px;
		}
	}
`

const StyledPrice = styled(TypographyPro)`
	color: var(--price);
	font-size: 16px;
	line-height: 19px;
`

const CartButton = styled.button`
	width: 135px;
	height: 55px;
	border: none;
	border-radius: var(--borderRadiusBtn);
	background-color: var(--primary);
	transition: 0.5s;
	box-shadow: var(--highlightShadow2);
	margin-top: 10px;
	cursor: pointer;
	position: relative;
	z-index: 1;

	& svg {
		transition: 0.5s;
	}

	&:active {
		background-color: var(--secondary);
		& svg {
			transform: translateY(5px);
		}
	}

	@media (hover: hover) {
		&:hover {
			background-color: var(--secondary);
		}
	}
`

const CartIcon = (props) => <IconComponent asset="/icons/cartMenuItem.svg" {...props} />

const StyledCartIcon = styled(CartIcon)`
	width: 33px;
	height: 33px;

	& path {
		fill: white;
	}
`

const LTOCard = ({ Application, Infra, item, locale, currency, hidePriceForUnlocalizedItem, ...other }) => {
	const imgUrl = item.media.logo // not actually a logo, but that is how the data is defined
	const title = getLocaleStr(item.title, locale)
	const description = getLocaleStr(item.description, locale)
	const { price } = item
	const displayPrice = item.description?.displayPrice
	const strikeoutPrice = item.strikeoutPrice ? item.strikeoutPrice : undefined

	return (
		<StyledCard {...other}>
			<CardMediaContainer
				data-testid="add-deal-pic"
				id={`${Application.page}-page-lto-${kebabCase(title)}-pic`}
				className="lto-media-container"
			>
				<CardMedia className="lto-media" component="img" image={imgUrl} alt={title} data-testid="lto-item-image" />
			</CardMediaContainer>

			<StyledCardContent>
				<TypographyPro className="lto-title text-two-lines" variant="h5" component="h5" data-testid="deal-item-title">
					{title}
				</TypographyPro>
				<TypographyPro className="lto-description text-two-lines" variant="BodyRegular" component="p" data-testid="lto-item-description">
					{description}
				</TypographyPro>
				<StyledPrice className="lto-price" variant="Price" component="div">
					<ItemPrice
						price={price}
						displayPrice={displayPrice}
						strikeoutPrice={strikeoutPrice}
						currency={currency}
						hideMenuItemPrice={hidePriceForUnlocalizedItem}
						countryCode={Infra.locale.region}
					/>
				</StyledPrice>
				<CartButton id={`${Application.page}-page-lto-${kebabCase(title)}-cart-icon`} data-testid="add-deal-item-button">
					<StyledCartIcon />
				</CartButton>
			</StyledCardContent>
		</StyledCard>
	)
}

export default inject('Application', 'Infra')(observer(LTOCard))
